import React, { ReactNode, useMemo } from "react";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { cx, useTranslations } from "@jugl-web/utils";
import { Button, ButtonProps } from "../../primitives/Button";
import errorAnimation from "./assets/error.json";
import googlyEyesAnimation from "./assets/no-results.json";
import boxAnimation from "./assets/no-results-box.json";
import financialAnimation from "./assets/no-results-financial.json";
import timeManagementAnimation from "./assets/time-management.json";

type CustomProps = {
  customTitle: React.ReactNode | null;
  customSubtitle: React.ReactNode;
  customImg?: React.ReactNode;
  className?: string;
  customButton?: Pick<
    ButtonProps,
    | "variant"
    | "color"
    | "isDisabled"
    | "iconStart"
    | "iconEnd"
    | "children"
    | "size"
    | "onClick"
    | "fullWidth"
    | "className"
  > & {
    text: string;
    onClick: () => void;
  };
  customButtonSlot?: ReactNode;
  onRefetch?: () => void;
};

type EmptyListErrorProps = Partial<CustomProps> & {
  type: "error";
  animation?: "error";
};

type EmptyListNoResultsProps = Partial<CustomProps> & {
  type: "noResults";
  animation?: "googlyEyes" | "box" | "financial" | "timeManagement";
};

type EmptyListCustomProps = CustomProps & {
  type: "custom";
};

export type EmptyListContentProps =
  | EmptyListCustomProps
  | EmptyListNoResultsProps
  | EmptyListErrorProps;

export const EmptyListContent: React.FC<EmptyListContentProps> = ({
  type,
  customButton,
  customButtonSlot,
  customImg,
  customSubtitle,
  customTitle,
  className,
  onRefetch,
  ...props
}) => {
  const { t } = useTranslations();

  const { image, title, subtitle, button } = useMemo(() => {
    const selectedAnimation: Record<string, unknown> = {
      googlyEyes: googlyEyesAnimation,
      error: googlyEyesAnimation,
      box: boxAnimation,
      financial: financialAnimation,
      timeManagement: timeManagementAnimation,
    };

    const animationData =
      "animation" in props && props.animation
        ? selectedAnimation[props.animation as keyof typeof selectedAnimation]
        : null;

    switch (type) {
      case "noResults":
        return {
          image: (
            <Lottie
              options={{
                animationData:
                  "animation" in props && props.animation
                    ? animationData
                    : googlyEyesAnimation,
              }}
              height={240}
              width={240}
            />
          ),
          title: t({
            id: "common.no-results",
            defaultMessage: "No results",
          }),
          subtitle: t({
            id: "empty-list-content-component.please-try-something-else",
            defaultMessage: "Please try something else",
          }),
        };
      case "error":
        return {
          image: (
            <Lottie
              options={{
                animationData:
                  "animation" in props && props.animation
                    ? animationData
                    : errorAnimation,
              }}
              height={180}
              width={180}
            />
          ),
          title: t({
            id: "common.something-went-wrong",
            defaultMessage: "Something went wrong",
          }),
          subtitle: t({
            id: "empty-list-content-component.click-button-to-retry",
            defaultMessage: "Click the button to retry",
          }),
          button: (
            <Button fullWidth onClick={onRefetch}>
              {t({
                id: "common.retry",
                defaultMessage: "Retry",
              })}
            </Button>
          ),
        };
      case "custom":
        return {};
      default:
        throw new Error("Invalid EmptyListContent type");
    }
  }, [onRefetch, props, t, type]);

  return (
    <div
      className={cx(
        "mx-auto flex h-full w-[280px] flex-col items-center justify-center gap-8 text-center font-secondary",
        className
      )}
    >
      {customImg || image}
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        {customTitle !== null && (
          <span className="font-secondary text-2xl font-medium text-black">
            {customTitle || title}
          </span>
        )}
        <span className="font-secondary text-[#828282]">
          {customSubtitle || subtitle}
        </span>
      </div>
      {customButtonSlot || (
        <>
          {customButton ? (
            <Button
              size="lg"
              {...customButton}
              className={customButton.className}
            >
              {customButton.text}
            </Button>
          ) : (
            button
          )}
        </>
      )}
    </div>
  );
};
